/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import { type XY, toStringBase, compare as compareXY, equals as equalsXY } from './xy'

/**
 * Construct a point
 */
export class Point implements XY {
	x: number
	y: number

	// All extra fields added to Point are prefixed with _p2t_
	// to avoid collisions if custom Point class is used.
	_p2t_edge_list: any[] | undefined = undefined

	constructor(x: number = 0, y: number = 0) {
		this.x = +x || 0
		this.y = +y || 0
	}

	/**
	 * For pretty printing
	 */
	toString(): string {
		return toStringBase(this)
	}

	/**
	 * JSON output, only coordinates
	 */
	toJSON() {
		return { x: this.x, y: this.y }
	}

	/**
	 * Creates a copy of this Point object.
	 */
	clone(): Point {
		return new Point(this.x, this.y)
	}

	/**
	 * Set this Point instance to the origin (0; 0)
	 */
	set_zero(): this {
		this.x = 0.0
		this.y = 0.0
		return this
	}

	/**
	 * Set the coordinates of this instance.
	 */
	set(x: number, y: number): this {
		this.x = +x || 0
		this.y = +y || 0
		return this
	}

	/**
	 * Negate this Point instance. (component-wise)
	 */
	negate(): this {
		this.x = -this.x
		this.y = -this.y
		return this
	}

	/**
	 * Add another Point object to this instance. (component-wise)
	 */
	add(n: XY): this {
		this.x += n.x
		this.y += n.y
		return this
	}

	/**
	 * Subtract this Point instance with another point given. (component-wise)
	 */
	sub(n: XY): this {
		this.x -= n.x
		this.y -= n.y
		return this
	}

	/**
	 * Multiply this Point instance by a scalar. (component-wise)
	 */
	mul(s: number): this {
		this.x *= s
		this.y *= s
		return this
	}

	/**
	 * Return the distance of this Point instance from the origin.
	 */
	length(): number {
		return Math.sqrt(this.x * this.x + this.y * this.y)
	}

	/**
	 * Normalize this Point instance (as a vector).
	 * @returns The original distance of this instance from the origin.
	 */
	normalize(): number {
		const len = this.length()
		this.x /= len
		this.y /= len
		return len
	}

	/**
	 * Test this Point object with another for equality.
	 */
	equals(p: XY): boolean {
		return this.x === p.x && this.y === p.y
	}

	// Static methods

	/**
	 * Negate a point component-wise and return the result as a new Point object.
	 */
	static negate(p: XY): Point {
		return new Point(-p.x, -p.y)
	}

	/**
	 * Add two points component-wise and return the result as a new Point object.
	 */
	static add(a: XY, b: XY): Point {
		return new Point(a.x + b.x, a.y + b.y)
	}

	/**
	 * Subtract two points component-wise and return the result as a new Point object.
	 */
	static sub(a: XY, b: XY): Point {
		return new Point(a.x - b.x, a.y - b.y)
	}

	/**
	 * Multiply a point by a scalar and return the result as a new Point object.
	 */
	static mul(s: number, p: XY): Point {
		return new Point(s * p.x, s * p.y)
	}

	/**
	 * Perform the cross product on either two points (this produces a scalar)
	 * or a point and a scalar (this produces a point).
	 */
	static cross(a: number | XY, b: number | XY): number | Point {
		if (typeof a === 'number') {
			if (typeof b === 'number') {
				return a * b
			} else {
				return new Point(-a * b.y, a * b.x)
			}
		} else {
			if (typeof b === 'number') {
				return new Point(b * a.y, -b * a.x)
			} else {
				return a.x * b.y - a.y * b.x
			}
		}
	}

	/**
	 * Perform the dot product on two vectors.
	 */
	static dotProd(a: XY, b: XY): number {
		return a.x * b.x + a.y * b.y
	}

	/**
	 * Compare two points component-wise. Ordered by y axis first, then x axis.
	 */
	static compare = compareXY

	/**
	 * Test two Point objects for equality.
	 */
	static equals = equalsXY
}

